import i18n from "../i18n/i18n";

/**
 * Translates an API message code to the current language
 * @param {string} messageCode - The message code from the API (e.g., 'USER_CREATED_SUCCESS')
 * @param {object} data - Optional data for message interpolation
 * @returns {string} The translated message
 */
export const translateApiMessage = (messageCode, data = {}) => {
  if (!messageCode) {
    return i18n.t("common.unknownError");
  }

  const key = `apiMessages.${messageCode}`;
  const translation = i18n.t(key, data);

  // If translation key doesn't exist, return the message code itself
  if (translation === key) {
    console.warn(`Missing translation for API message code: ${messageCode}`);
    return messageCode.replace(/_/g, " ").toLowerCase();
  }

  return translation;
};

/**
 * Processes API response and extracts the appropriate message
 * @param {object} response - The API response object (Axios response or response.data)
 * @returns {string} The translated message
 */
export const getApiResponseMessage = (response) => {
  // If it's an Axios response object (has data property), use response.data
  const data = response?.data || response;

  // Check if response has messageCode (new multi-language approach)
  if (data?.messageCode) {
    return translateApiMessage(data.messageCode, data.responseObject);
  }

  // Fallback to message field for backward compatibility
  if (data?.message) {
    return data.message;
  }

  return i18n.t("common.unknownError");
};

/**
 * Handles API errors and returns appropriate translated messages
 * @param {object} error - The error object from axios or API
 * @returns {string} The translated error message
 */
export const getApiErrorMessage = (error) => {
  // Check if error response has messageCode
  if (error?.response?.data?.messageCode) {
    return translateApiMessage(
      error.response.data.messageCode,
      error.response.data.responseObject,
    );
  }

  // Fallback to message field
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle network errors
  if (!error?.response) {
    return i18n.t("auth.login.connectionError");
  }

  // Handle HTTP status codes
  switch (error?.response?.status) {
    case 401:
      return i18n.t("auth.login.unauthorizedWarning");
    case 403:
      return i18n.t("auth.login.invalidCredentialsError");
    case 404:
      return i18n.t("apiMessages.RECORD_NOT_FOUND");
    case 500:
      return i18n.t("apiMessages.INTERNAL_SERVER_ERROR");
    default:
      return i18n.t("common.unknownError");
  }
};
